<template>
  <section class="christmas-tree" :class="{ 'is-celebrating': celebrating }" aria-label="裝飾聖誕樹">
    <div ref="tree" class="christmas-tree-target">
      <span class="christmas-tree-trunk"></span>
      <span class="christmas-tree-branch branch-bottom"></span>
      <span class="christmas-tree-branch branch-middle"></span>
      <span class="christmas-tree-branch branch-top"></span>
      <button
        v-for="item in decorations"
        :key="item.id"
        class="christmas-decoration placed is-glowing-ornament"
        :class="'ornament-' + item.type"
        :style="{ left: item.x + 'px', top: item.y + 'px', '--ornament-glow': item.glow, visibility: dragged && dragged.id === item.id ? 'hidden' : 'visible' }"
        :aria-label="item.name + '，可拖曳調整位置'"
        @pointerdown.prevent.stop="startDrag($event, item.type, item.id)"
      >{{ item.symbol }}</button>
    </div>
    <div class="christmas-decoration-tray">
      <button
        v-for="(item, type) in ornamentTypes"
        :key="type"
        class="christmas-decoration"
        :class="'ornament-' + type"
        :style="{ '--ornament-glow': item.glow, visibility: usedTypes.includes(type) || (dragged && dragged.type === type) ? 'hidden' : 'visible' }"
        :aria-label="item.name + '，拖到樹上或按 Enter 放置'"
        :title="item.name"
        @pointerdown.prevent.stop="startDrag($event, type)"
        @keydown.enter.prevent="addDecoration(type, 90, 70 + decorations.length * 28)"
        @keydown.space.prevent="addDecoration(type, 90, 70 + decorations.length * 28)"
      >{{ item.symbol }}</button>
    </div>
    <span
      v-if="dragged"
      class="christmas-decoration christmas-decoration-dragging"
      :class="'ornament-' + dragged.type"
      :style="{ left: dragged.x + 'px', top: dragged.y + 'px', '--ornament-glow': ornamentTypes[dragged.type].glow }"
      aria-hidden="true"
    >{{ ornamentTypes[dragged.type].symbol }}</span>
    <div v-if="celebrating" class="christmas-celebration-backdrop" aria-hidden="true"></div>
    <div v-if="celebrating" class="christmas-snowfall" aria-hidden="true">
      <i
        v-for="flake in snowflakes"
        :key="flake.id"
        class="christmas-snowflake"
        :style="{ left: flake.left + '%', width: flake.size + 'px', height: flake.size + 'px', animationDelay: flake.delay + 's', '--flake-drift': flake.drift + 'px', '--flake-turn': flake.turn + 'deg' }"
      ></i>
    </div>
    <span class="sr-only" role="status">{{ celebrating ? '聖誕樹布置完成！聖誕快樂！' : '' }}</span>
  </section>
</template>

<script>
export default {
  name: 'ChristmasTree',
  data () {
    return {
      ornamentTypes: {
        red: { name: '紅色彩球', symbol: '🔴', glow: '#FF3535' },
        gold: { name: '金色彩球', symbol: '🟡', glow: '#FFD53D' },
        blue: { name: '藍色彩球', symbol: '🔵', glow: '#328BFF' },
        star: { name: '星星', symbol: '⭐', glow: '#E8BC65' },
        bow: { name: '蝴蝶結', symbol: '🎀', glow: '#FF69A0' },
        bell: { name: '鈴鐺', symbol: '🔔', glow: '#FFD53D' }
      },
      decorations: [],
      usedTypes: [],
      dragged: null,
      nextId: 0,
      celebrating: false,
      celebrationTimer: null,
      snowflakes: Array.from({ length: 80 }, (_, id) => ({
        id,
        left: (id * 37 % 100),
        size: 5 + id % 5 * 2,
        delay: (id % 20) * 0.15,
        drift: (id % 2 ? 1 : -1) * (40 + id % 5 * 25),
        turn: (id % 2 ? 1 : -1) * (360 + id % 4 * 180)
      }))
    }
  },
  mounted () {
    window.addEventListener('pointermove', this.moveDrag)
    window.addEventListener('pointerup', this.placeDecoration)
    window.addEventListener('pointercancel', this.cancelDrag)
    window.addEventListener('blur', this.cancelDrag)
  },
  beforeDestroy () {
    window.clearTimeout(this.celebrationTimer)
    window.removeEventListener('pointermove', this.moveDrag)
    window.removeEventListener('pointerup', this.placeDecoration)
    window.removeEventListener('pointercancel', this.cancelDrag)
    window.removeEventListener('blur', this.cancelDrag)
  },
  methods: {
    startDrag (event, type, id = null) {
      if (event.button !== 0 || this.dragged || (id === null && this.usedTypes.includes(type))) return
      const bounds = event.currentTarget.getBoundingClientRect()
      this.dragged = {
        type,
        id,
        pointerId: event.pointerId,
        offsetX: event.clientX - bounds.left - bounds.width / 2,
        offsetY: event.clientY - bounds.top - bounds.height / 2,
        x: bounds.left + bounds.width / 2,
        y: bounds.top + bounds.height / 2
      }
    },
    moveDrag (event) {
      if (!this.dragged || event.pointerId !== this.dragged.pointerId) return
      this.dragged.x = event.clientX - this.dragged.offsetX
      this.dragged.y = event.clientY - this.dragged.offsetY
    },
    cancelDrag () {
      this.dragged = null
    },
    addDecoration (type, x, y) {
      if (this.usedTypes.includes(type)) return
      this.decorations.push({ ...this.ornamentTypes[type], type, id: this.nextId++, x, y })
      this.usedTypes.push(type)
      if (this.usedTypes.length === Object.keys(this.ornamentTypes).length) {
        this.celebrating = true
        this.celebrationTimer = window.setTimeout(() => {
          this.celebrating = false
          this.celebrationTimer = null
        }, 8000)
      }
    },
    placeDecoration (event) {
      if (!this.dragged || event.pointerId !== this.dragged.pointerId) return
      this.moveDrag(event)
      const bounds = this.$refs.tree.getBoundingClientRect()
      const scale = bounds.width / 180
      let x = (this.dragged.x - bounds.left) / scale
      let y = (this.dragged.y - bounds.top) / scale
      const edgePadding = 24
      const nearTreeTop = this.dragged.type === 'star' && Math.abs(x - 90) <= 48 && y >= -48 && y <= 48
      const onTree = [
        { top: 0, height: 135, width: 110 },
        { top: 45, height: 145, width: 145 },
        { top: 92, height: 160, width: 180 }
      ].some(layer => y >= layer.top - edgePadding && y <= layer.top + layer.height + edgePadding &&
        Math.abs(x - 90) <= Math.min(layer.height, Math.max(0, y - layer.top)) / layer.height * layer.width / 2 + edgePadding)
      if (nearTreeTop) {
        x = 90
        y = 0
      }
      if (onTree || nearTreeTop) {
        const existing = this.decorations.find(item => item.id === this.dragged.id)
        if (existing) {
          existing.x = x
          existing.y = y
        } else {
          this.addDecoration(this.dragged.type, x, y)
        }
      }
      this.cancelDrag()
    }
  }
}
</script>
